import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi/useApi.ts";
import type { SearchFilterParams } from "~pages/Persons/PersonListPage/types/searchFilterParams.ts";
import type { FixturePlayByPlayEventTransformed } from "~pages/Reports/components/FixturePlayByPlayFlaggedEventDataTable/types/FixturePlayByPlayEventTransformed.ts";
import type { FixturePlayByPlayFlaggedEventResponse } from "~types/fixturePlayByPlayEvent.ts";
import { buildFixtureName } from "~utilities/buildFixtureName.ts";
import { removeEmptyFields } from "~utils/removeEmptyFields.ts";
import { transformListResponse } from "~utils/transformListResponse.ts";

const useFetchFixturePlayByPlayFlaggedEvents = (params: SearchFilterParams, enabled = true) => {
  const request = useApi();
  const { organizationId, sport, seasonId } = useParams();
  const { formatMessage } = useIntl();

  const url = `${sport}/o/${organizationId}/playbyplay/flagged?seasonId=${seasonId}&${new URLSearchParams(removeEmptyFields(params) as Record<string, string>).toString()}`;

  return useQuery({
    queryKey: ["fixturePlayByPlayFlaggedEvents", sport, organizationId, seasonId, params],
    enabled: enabled,
    queryFn: async () =>
      request<FixturePlayByPlayFlaggedEventResponse>(url).then((response) => {
        const persons = response?.includes?.resources?.persons || {};
        const fixtures = response?.includes?.resources?.fixtures || {};
        const entities = response?.includes?.resources?.entities || {};
        const data = (response?.data || []).map((event) => {
          return {
            ...event,
            person: event?.person?.id ? persons[event?.person?.id] : null,
            fixture: event?.fixture?.id
              ? {
                  ...fixtures[event?.fixture?.id],
                  nameLocal: buildFixtureName(
                    fixtures[event?.fixture?.id],
                    response?.includes?.resources,
                    formatMessage({ id: "not.specified" }),
                  ),
                }
              : null,
            entity: event?.entity?.id ? entities[event?.entity?.id] : null,
          };
        });
        return transformListResponse({ ...response, data: data as FixturePlayByPlayEventTransformed[] });
      }),
  });
};

export default useFetchFixturePlayByPlayFlaggedEvents;
